import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Card, Badge, Filter, Search, KPICard, Table, Avatar, FeaturePlaceholder, Alert } from '@/components/ui';
import { useConstructionSetup, useConstructionMembers, useConstructionDocuments } from './hooks/useConstructionSetup';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';

const setupStatusColors: Record<string, string> = {
  not_started: 'bg-slate-100 text-slate-600',
  in_progress: 'bg-blue-100 text-blue-600',
  missing_documents: 'bg-amber-100 text-amber-600',
  missing_team: 'bg-orange-100 text-orange-600',
  ready_for_boq: 'bg-emerald-100 text-emerald-600',
  ready_for_construction: 'bg-emerald-900 text-white',
  blocked: 'bg-red-100 text-red-600',
};

const setupStatusLabels: Record<string, string> = {
  not_started: 'Chưa bắt đầu',
  in_progress: 'Đang thiết lập',
  missing_documents: 'Thiếu hồ sơ',
  missing_team: 'Thiếu nhân sự',
  ready_for_boq: 'Sẵn sàng lập BOQ',
  ready_for_construction: 'Sẵn sàng thi công',
  blocked: 'Đang tạm dừng',
};

export default function ConstructionSetupPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const {
    settings,
    checks,
    isLoadingSettings,
    isLoadingChecks,
    completeCheck,
    isCompletingCheck,
    updateSettings,
  } = useConstructionSetup(id!);

  const { members, isLoading: isLoadingMembers } = useConstructionMembers(id!);
  const { data: documents, isLoading: isLoadingDocs } = useConstructionDocuments(id!);

  const [activeTab, setActiveTab] = useState('overview');

  const isLoading = isLoadingSettings || isLoadingChecks;

  if (!id) return null;

  return (
    <PageLayout
      title="Thiết lập Dự án Thi công"
      description="Chuẩn bị các điều kiện cần thiết để đưa dự án vào giai đoạn lập BOQ và triển khai thực địa."
      breadcrumbs={[
        { label: 'Dự án', path: '/projects' },
        { label: 'Thiết lập thi công' }
      ]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-settings-line">Cấu hình chung</Button>
          <AuthButton
            module="construction"
            action="mark_ready"
            variant="primary"
            icon="ri-checkbox-circle-line"
            disabled={settings?.setup_status === 'ready_for_boq'}
            onClick={() => updateSettings({ setup_status: 'ready_for_boq' })}
          >
            Sẵn sàng lập BOQ
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Status Banner */}
        <Card className={`p-6 border-none shadow-sm flex items-center justify-between ${settings?.setup_status === 'ready_for_boq' ? 'bg-emerald-50' : 'bg-blue-50'}`}>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white ${settings?.setup_status === 'ready_for_boq' ? 'bg-emerald-500' : 'bg-os-primary'}`}>
              <i className={settings?.setup_status === 'ready_for_boq' ? 'ri-shield-check-line text-2xl' : 'ri-tools-line text-2xl'} />
            </div>
            <div>
              <h3 className="font-bold text-slate-900">Trạng thái chuẩn bị: {setupStatusLabels[settings?.setup_status || 'not_started']}</h3>
              <p className="sm:text-sm text-xs text-slate-500 mt-1">Hoàn thành {checks.filter(c => c.is_completed).length}/{checks.length} bước trong checklist thiết lập thi công.</p>
            </div>
          </div>
          <Badge className={`${setupStatusColors[settings?.setup_status || 'not_started']} border-transparent text-[10px] font-bold px-3 py-1`}>
            {settings?.setup_status || 'not_started'}
          </Badge>
        </Card>

        <div className="flex gap-4 border-b border-slate-100 overflow-x-auto no-scrollbar pb-px">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-4 sm:text-sm text-xs font-normal transition-all border-b-2 whitespace-nowrap ${activeTab === 'overview' ? 'border-os-primary text-os-primary' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
          >
            Tổng quan & Checklist
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-6 py-4 sm:text-sm text-xs font-normal transition-all border-b-2 whitespace-nowrap ${activeTab === 'members' ? 'border-os-primary text-os-primary' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
          >
            Nhân sự Dự án ({members.length})
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-6 py-4 sm:text-sm text-xs font-normal transition-all border-b-2 whitespace-nowrap ${activeTab === 'documents' ? 'border-os-primary text-os-primary' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
          >
            Hồ sơ Thiết kế ({documents?.length || 0})
          </button>
        </div>

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-list-check-2 text-os-primary" />
                  Checklist Thiết lập Thi công
                </h3>
                <div className="space-y-4">
                  {checks.map((check) => (
                    <div
                      key={check.id}
                      className={`flex items-center gap-4 p-4 rounded-2xl border transition-all ${check.is_completed ? 'bg-slate-50/50 border-emerald-100' : 'bg-white border-slate-100 hover:border-os-primary/30 shadow-sm'}`}
                    >
                      <button
                        className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${check.is_completed ? 'bg-emerald-500 text-white shadow-emerald-200 shadow-md' : 'border-2 border-slate-200 text-transparent'}`}
                        onClick={() => !check.is_completed && completeCheck({ checkId: check.id, userId: profile?.id || '' })}
                        disabled={isCompletingCheck || check.is_completed}
                      >
                        <i className="ri-check-line sm:text-sm text-xs font-normal" />
                      </button>
                      <div className="flex-1">
                        <p className={`sm:text-sm text-xs font-normal ${check.is_completed ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{check.check_label}</p>
                        {check.is_completed && (
                          <p className="text-[10px] text-slate-400 mt-0.5">Xác nhận bởi {check.completed_by_user?.full_name} • {new Date(check.completed_at!).toLocaleDateString('vi-VN')}</p>
                        )}
                      </div>
                      <Badge variant="neutral" className="bg-slate-100 text-slate-400 text-[9px] font-bold">{check.check_code}</Badge>
                    </div>
                  ))}
                </div>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-6">
                  <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <i className="ri-calendar-event-line text-slate-400" />
                    Lịch làm việc (Calendar)
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between sm:text-sm text-xs">
                      <span className="text-slate-500">Giờ vào ca</span>
                      <span className="font-bold text-slate-700">{settings?.default_work_start_time || '07:30'}</span>
                    </div>
                    <div className="flex justify-between sm:text-sm text-xs">
                      <span className="text-slate-500">Giờ tan ca</span>
                      <span className="font-bold text-slate-700">{settings?.default_work_end_time || '17:00'}</span>
                    </div>
                    <div className="flex justify-between sm:text-sm text-xs">
                      <span className="text-slate-500">Ngày làm việc</span>
                      <span className="font-bold text-os-primary">Thứ 2 - Thứ 7</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="w-full mt-4 sm:text-sm text-xs font-normal text-os-primary">Điều chỉnh lịch</Button>
                </Card>

                <Card className="p-6">
                  <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <i className="ri-shield-check-line text-slate-400" />
                    Tiêu chuẩn QA & An toàn
                  </h4>
                  <div className="space-y-3">
                    <div className="p-2 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-3">
                      <i className="ri-book-read-line text-os-primary" />
                      <span className="sm:text-sm text-xs font-normal text-slate-700">Tiêu chuẩn Cây xanh v2.0</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-3">
                      <i className="ri-shield-user-line text-orange-500" />
                      <span className="sm:text-sm text-xs font-normal text-slate-700">Quy định HSE Công trường</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="w-full mt-4 sm:text-sm text-xs font-normal text-os-primary">Xem chi tiết</Button>
                </Card>
              </div>
            </div>

            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-user-settings-line text-os-primary" />
                  Ban Chỉ huy Công trường
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Avatar size="sm" name="Project Manager" />
                    <div>
                      <p className="sm:text-sm text-xs font-normal text-slate-900">Project Manager (PM)</p>
                      <p className="text-[10px] text-slate-500">Chưa xác định</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Avatar size="sm" name="Site Manager" />
                    <div>
                      <p className="sm:text-sm text-xs font-normal text-slate-900">Site Manager (SM)</p>
                      <p className="text-[10px] text-slate-500">Chưa xác định</p>
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="w-full mt-6" icon="ri-user-add-line">Giao nhân sự chính</Button>
              </Card>

              <Card className="p-6 bg-slate-900 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Thông tin hiện trường
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 tracking-widest">Mã thi công</label>
                    <p className="sm:text-sm text-xs font-normal text-os-primary font-mono">{settings?.construction_code || '---'}</p>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 tracking-widest">Loại hình</label>
                    <p className="sm:text-sm text-xs font-normal">{settings?.construction_type || 'Landscape Construction'}</p>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 tracking-widest">Điều kiện mặt bằng</label>
                    <p className="sm:text-sm text-xs text-slate-400 italic">"{settings?.site_condition || 'Đang cập nhật khảo sát...'}"</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {(activeTab === 'members' || activeTab === 'documents') && (
          <FeaturePlaceholder
            title={activeTab === 'members' ? 'Nhân sự dự án' : 'Hồ sơ thiết kế'}
            description={activeTab === 'members' ? 'Quản lý toàn bộ ban chỉ huy, tổ đội và nhân sự trực thuộc dự án thi công.' : 'Hệ thống lưu trữ và phê duyệt bản vẽ thiết kế, shop drawing và các tài liệu kỹ thuật thi công.'}
          />
        )}
      </div>
    </PageLayout>
  );
}
